import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useCallback, useReducer } from 'react';
import { useTranslation } from 'react-i18next';

import { forgotPassword } from './forgotPassword';

/**
 * React hook to handle forgot password logic with toast notifications.
 */
type ForgotPasswordState = {
  isLoading: boolean;
  isSuccess: boolean;
  isError: boolean;
  error: unknown;
};

type ForgotPasswordAction =
  | { type: 'LOADING' }
  | { type: 'SUCCESS' }
  | { type: 'ERROR'; error: unknown }
  | { type: 'RESET' };

const initialForgotPasswordState: ForgotPasswordState = {
  isLoading: false,
  isSuccess: false,
  isError: false,
  error: null,
};

function forgotPasswordReducer(state: ForgotPasswordState, action: ForgotPasswordAction): ForgotPasswordState {
  switch (action.type) {
    case 'LOADING':
      return { isLoading: true, isSuccess: false, isError: false, error: null };
    case 'SUCCESS':
      return { ...state, isLoading: false, isSuccess: true };
    case 'ERROR':
      return { ...state, isLoading: false, isError: true, error: action.error };
    case 'RESET':
      return initialForgotPasswordState;
    default:
      return state;
  }
}

export function useForgotPassword() {
  const { t } = useTranslation('login');
  const [state, dispatch] = useReducer(forgotPasswordReducer, initialForgotPasswordState);

  const handleForgotPassword = useCallback(
    (email: string) => {
      dispatch({ type: 'LOADING' });

      toast.promise(forgotPassword(email), {
        duration: Infinity,
        loading: t('form.labels.sending'),
        success: () => {
          dispatch({ type: 'SUCCESS' });

          return t('form.labels.forgot_password_success', { email });
        },
        error: err => {
          dispatch({ type: 'ERROR', error: err });

          return err?.data?.detail || t('form.labels.forgot_password_error');
        },
      });
    },
    [t],
  );

  return {
    handleForgotPassword,
    isLoading: state.isLoading,
    isSuccess: state.isSuccess,
    isError: state.isError,
    error: state.error,
  };
}
